<template>
    <div class="top-search">
        <input type="text" placeholder="Search" v-model="q" @input="onInput()" @focus="open = !!q.trim()" @keydown="onKeydown($event)" />
        <div v-if="open && q.trim().length >= 3" class="top-search-results">
            <a v-for="(node, i) in results" :key="node.Slug" :class="i == highlighted ? 'on' : ''" @mousedown.prevent="select(node)" @mouseenter="highlighted = i">
                <span class="top-search-name">{{ node.Name }}</span>
                <span class="top-search-type">{{ typeLabel(node.Type) }}</span>
                <span v-if="node.Reason" class="top-search-reason">{{ node.Reason }}</span>
            </a>
            <div v-if="loading" class="top-search-status">Searching…</div>
            <div v-else-if="error" class="top-search-status">{{ error }}</div>
            <div v-else-if="!results.length" class="top-search-status">No matches found</div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

const TYPE_LABELS: any = {
    PROCESS: 'Process', CHECKLIST: 'Checklist', SHEET: 'Role Sheet',
    TIMELINE: 'Value Engine', VIDEO: 'Video', LINK: 'Link'
};

export default defineComponent({
    name: 'TopSearch',
    data() {
        return {
            q: '',
            open: false,
            loading: false,
            error: '',
            results: [] as any[],
            highlighted: -1,
            timer: null as any,
            requestID: 0,
            aiCache: {} as Record<string, any[]>
        }
    },
    methods: {
        typeLabel(type: string) {
            return TYPE_LABELS[type] || type;
        },
        // Instant name matches while typing; AI results replace them after a short pause
        onInput() {
            clearTimeout(this.timer);
            this.requestID++;
            this.error = '';
            this.highlighted = -1;
            const q = this.q.trim();
            this.open = !!q;
            if (q.length < 3) {
                this.results = [];
                this.loading = false;
                return;
            }
            const qLower = q.toLowerCase();
            if (this.aiCache[qLower]) {
                this.results = this.aiCache[qLower];
                this.loading = false;
                return;
            }
            this.results = (this.$store.state.allNodes || [])
                .filter((node: any) => node.Type != 'NODE' && node.Name.toLowerCase().includes(qLower))
                .slice(0, 8);
            this.loading = true;
            this.timer = setTimeout(() => this.aiSearch(), 700);
        },
        async aiSearch() {
            clearTimeout(this.timer);
            const q = this.q.trim();
            const qLower = q.toLowerCase();
            if (q.length < 3 || this.aiCache[qLower]) return;
            const requestID = ++this.requestID;
            this.loading = true;
            await axios.get(`${this.$store.state.apiUrl}/api/search`, { params: { q } })
                .then(resp => {
                    if (requestID != this.requestID) return;
                    this.aiCache[qLower] = resp.data;
                    this.results = resp.data;
                    this.highlighted = -1;
                })
                .catch(e => {
                    if (requestID != this.requestID) return;
                    console.log(e);
                    this.error = e.response?.status == 429 ? e.response.data : 'Search is unavailable right now';
                })
            if (requestID == this.requestID) this.loading = false;
        },
        onKeydown(e: KeyboardEvent) {
            if (e.key == 'Escape') {
                this.open = false;
            } else if (e.key == 'ArrowDown' && this.results.length) {
                e.preventDefault();
                this.open = true;
                this.highlighted = (this.highlighted + 1) % this.results.length;
            } else if (e.key == 'ArrowUp' && this.results.length) {
                e.preventDefault();
                this.highlighted = this.highlighted <= 0 ? this.results.length - 1 : this.highlighted - 1;
            } else if (e.key == 'Enter') {
                if (this.highlighted >= 0 && this.results[this.highlighted]) {
                    this.select(this.results[this.highlighted]);
                } else {
                    this.open = true;
                    this.aiSearch();
                }
            }
        },
        select(node: any) {
            this.open = false;
            this.q = '';
            this.results = [];
            if (node.Type == 'LINK') {
                window.open(node.Link, '_blank');
                return;
            }
            this.$router.push('/playbook/' + node.Slug);
        },
        handleOutsideClick(e: MouseEvent) {
            if (this.open && !(e.target as HTMLElement).closest('.top-search')) {
                this.open = false;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeUnmount() {
        clearTimeout(this.timer);
        document.removeEventListener('click', this.handleOutsideClick);
    }
});
</script>
